@if (isLoading) {
  <div class="sales-view__loading"><mat-spinner diameter="40"></mat-spinner></div>
} @else {
  <div class="sales-view">
    <div class="sales-view__bar">
      <button mat-button class="sales-view__back" (click)="backToList()">
        <mat-icon>chevron_left</mat-icon>{{ 'salesView.backToList' | translate }}
      </button>
      <span class="sales-view__count">
        {{ (offerCount === 1 ? 'salesView.countOne' : 'salesView.count') | translate: {count: offerCount} }}
      </span>
    </div>

    <div class="sales-view__intro">
      <h1 class="sales-view__title">{{ 'salesView.title' | translate }}</h1>
      <p class="sales-view__subtitle">{{ 'salesView.subtitle' | translate }}</p>
    </div>

    <div class="sales-view__filters">
      <app-ci-multiselect
        class="sales-view__filter"
        variant="outline"
        [items]="submittedByFilterOptions"
        [summary]="true"
        [summaryTemplate]="'ciSelect.selectedOfTotal' | translate"
        [placeholder]="'salesView.filter.allSubmittedBy' | translate"
        [label]="'salesView.filter.submittedBy' | translate"
        [ariaLabel]="'salesView.filter.submittedBy' | translate"
        [searchPlaceholder]="'ciSelect.search' | translate"
        [clearLabel]="'ciSelect.clearSelection' | translate"
        [emptyLabel]="'ciSelect.noMatch' | translate"
        [(ngModel)]="submittedByFilter"
        (ngModelChange)="onFilterChange()"
      ></app-ci-multiselect>

      <app-ci-multiselect
        class="sales-view__filter"
        variant="outline"
        [items]="probabilityFilterOptions"
        [displayWith]="displayProbabilityFilter"
        [summary]="true"
        [summaryTemplate]="'ciSelect.selectedOfTotal' | translate"
        [placeholder]="'salesView.filter.allProbability' | translate"
        [label]="'salesView.filter.probability' | translate"
        [ariaLabel]="'salesView.filter.probability' | translate"
        [searchPlaceholder]="'ciSelect.search' | translate"
        [clearLabel]="'ciSelect.clearSelection' | translate"
        [emptyLabel]="'ciSelect.noMatch' | translate"
        [(ngModel)]="probabilityFilter"
        (ngModelChange)="onFilterChange()"
      ></app-ci-multiselect>

      <app-ci-multiselect
        class="sales-view__filter"
        variant="outline"
        [items]="buFilterOptions"
        [displayWith]="displayBuFilter"
        [summary]="true"
        [summaryTemplate]="'ciSelect.selectedOfTotal' | translate"
        [placeholder]="'salesView.filter.allBus' | translate"
        [label]="'salesView.filter.bu' | translate"
        [ariaLabel]="'salesView.filter.buAria' | translate"
        [searchPlaceholder]="'ciSelect.search' | translate"
        [clearLabel]="'ciSelect.clearSelection' | translate"
        [emptyLabel]="'ciSelect.noMatch' | translate"
        [(ngModel)]="buFilter"
        (ngModelChange)="onFilterChange()"
      ></app-ci-multiselect>

      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="sales-view__search">
        <mat-label>{{ 'salesView.filter.search' | translate }}</mat-label>
        <input matInput type="search" [(ngModel)]="searchText" (ngModelChange)="onSearchChange()"/>
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    </div>

    @if (offerCount === 0) {
      <div class="sales-view__empty">{{ 'salesView.empty' | translate }}</div>
    } @else {
      <div class="sales-view__table-wrapper">
        <table class="sales-view__table">
          <thead>
            <tr>
              @for (col of sortColumns; track col.key) {
                <th class="sales-view__th" scope="col" tabindex="0"
                    [attr.aria-sort]="sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'"
                    (click)="onSort(col.key)" (keydown.enter)="onSort(col.key)">
                  {{ col.labelKey | translate }}
                  <span class="sales-view__sort" [class.sales-view__sort--active]="sortKey === col.key">
                    {{ sortIndicator(col.key) }}
                  </span>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of visibleRows; track row.offer) {
              <tr class="sales-view__row" (click)="openDetail(row.entry)">
                <td>
                  <div class="sales-view__person">
                    @if (row.entry.consultantId && row.entry.photoId) {
                      <img class="sales-view__avatar sales-view__avatar--photo"
                           [lazyImage]="row.entry.consultantId" [lazyImagePhotoId]="row.entry.photoId"
                           [alt]="row.entry.fullName"/>
                    } @else {
                      <span class="sales-view__avatar">{{ getInitials(row.entry.fullName) }}</span>
                    }
                    <span class="sales-view__person-text">
                      <span class="sales-view__person-name">{{ row.entry.fullName }}</span>
                      @if (row.entry.abbreviation) {
                        <span class="sales-view__kuerzel">{{ row.entry.abbreviation }}</span>
                      }
                    </span>
                  </div>
                </td>
                <td>
                  @if (isBenchFirm(row.entry.firm)) {
                    <app-company-badge [abbreviation]="row.entry.firm"></app-company-badge>
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td class="sales-view__role">
                  @if (row.offer.role) {
                    {{ row.offer.role }}
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td>
                  @if (row.offer.customer) {
                    {{ row.offer.customer }}
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td>
                  @if (row.offer.intermediary) {
                    {{ row.offer.intermediary }}
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td class="sales-view__bu"><app-bu-badge [bu]="buFor(row.offer)"></app-bu-badge></td>
                <td class="sales-view__cond">
                  @if (conditionsLabel(row.offer); as cond) {
                    {{ cond }}
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td>
                  @if (row.submitter.original) {
                    <span class="sales-view__submitter" [matTooltip]="row.submitter.tooltip"
                          [matTooltipDisabled]="!row.submitter.substituted">
                      @if (row.submitter.substituted) {
                        <span class="sales-view__submitter-absent">{{ row.submitter.original }}</span>
                        <span class="sales-view__submitter-deputy">{{ row.submitter.active }}</span>
                      } @else {
                        {{ row.submitter.original }}
                      }
                    </span>
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td>
                  @if (row.offer.probability != null) {
                    <span class="sales-view__wk-badge"
                          [ngClass]="'sales-view__wk-badge--' + row.offer.probability">{{ row.offer.probability }} %</span>
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
                <td>
                  <app-extension-badge [extension]="row.offer.extension" [extendedUntil]="row.offer.extendedUntil"></app-extension-badge>
                </td>
                <td>
                  <span class="sales-view__status-tag"
                        [ngClass]="'sales-view__status-tag--' + row.offer.status?.toLowerCase()">
                    {{ ('benchOverview.status.' + row.offer.status?.toLowerCase()) | translate }}
                  </span>
                </td>
                <td class="sales-view__date">
                  @if (row.offer.date) {
                    {{ row.offer.date | date: 'dd.MM.yyyy' }}
                  } @else {
                    <span class="sales-view__empty">—</span>
                  }
                </td>
              </tr>
              <tr class="sales-view__comments-row">
                <td [attr.colspan]="sortColumns.length">
                  <div class="sales-view__comments">
                    <button type="button" class="sales-view__comments-toggle"
                            [attr.aria-expanded]="row.commentsOpen"
                            (click)="row.commentsOpen = !row.commentsOpen">
                      <mat-icon>{{ row.commentsOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
                      <span>{{ 'salesView.comments.label' | translate }}</span>
                      @if (row.offer.notes.length) {
                        <span class="sales-view__comments-badge">{{ row.offer.notes.length }}</span>
                      }
                    </button>

                    @if (row.commentsOpen) {
                      @for (note of row.offer.notes; track note) {
                        <div class="sales-view__comment-item">
                          @if (note.date) {
                            <span class="sales-view__comment-date">{{ note.date | date: 'dd.MM.yyyy' }}</span>
                          }
                          @if (note.author) {
                            <span class="sales-view__comment-author">{{ note.author }}</span>
                          }
                          <span class="sales-view__comment-text">{{ note.text }}</span>
                          <button mat-icon-button type="button" class="sales-view__comment-remove"
                                  (click)="askRemoveComment(row, note)"
                                  [matTooltip]="'salesView.comments.remove' | translate"
                                  [attr.aria-label]="'salesView.comments.remove' | translate">
                            <mat-icon>delete</mat-icon>
                          </button>
                        </div>
                      }
                      <div class="sales-view__comment-input">
                        <input class="sales-view__comment-textbox" type="text"
                               [(ngModel)]="row.commentDraft"
                               [placeholder]="'salesView.comments.placeholder' | translate"
                               (keydown.enter)="addComment(row)"/>
                        <button mat-flat-button type="button" class="sales-view__comment-save"
                                [disabled]="!row.commentDraft.trim() || savingOffer === row.offer"
                                (click)="addComment(row)">
                          {{ 'salesView.comments.add' | translate }}
                        </button>
                      </div>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

  </div>

  <app-modal
    [visible]="!!pendingCommentRemoval"
    [showClose]="true"
    [title]="'salesView.comments.removeConfirm.title'"
    [text]="'salesView.comments.removeConfirm.text'"
    [saveText]="'general.delete'"
    (save)="confirmRemoveComment()"
    (cancel)="cancelRemoveComment()"
  ></app-modal>
}
